/**
 * Recovery for stale lazy chunks after a deploy.
 *
 * A tab that is open across a deploy still holds the previous entry chunk and will ask for
 * asset filenames the new build no longer has, so every dynamic import (route modules, lazy
 * components) fails with `Failed to fetch dynamically imported module`. Vite fires
 * `vite:preloadError` on window for exactly this case.
 *
 * Reload at most ONCE per build: if the chunk is still missing after the reload, the problem
 * is not a stale tab (CDN outage, bad asset path) and reloading again would loop. In that
 * case the event is left unprevented so it reaches Sentry.
 */

export const PRELOAD_RELOAD_KEY = 'ts_preload_reload'

interface PreloadRecoveryDeps {
  storage?: Storage
  reload?: () => void
}

/** Build-scoped so a later deploy naturally starts with a fresh marker. */
function reloadKey (): string {
  const build = typeof __COMMIT_HASH__ === 'string' ? __COMMIT_HASH__ : 'dev'
  return `${PRELOAD_RELOAD_KEY}:${build}`
}

function getStorage (explicit?: Storage): Storage | null {
  if (explicit) {
    return explicit
  }
  try {
    return window.sessionStorage
  } catch {
    return null // storage blocked (private mode / third-party cookie policy)
  }
}

/**
 * @returns true when a reload was triggered (and the event suppressed).
 */
export function handlePreloadError (event: Event, deps: PreloadRecoveryDeps = {}): boolean {
  const storage = getStorage(deps.storage)
  const key = reloadKey()

  let alreadyReloaded = false
  try {
    alreadyReloaded = storage?.getItem(key) === '1'
  } catch {
    alreadyReloaded = false
  }

  if (alreadyReloaded) {
    // Persistent failure — let it surface.
    return false
  }

  // The marker MUST survive the reload, otherwise every reload fails the same import,
  // finds no marker, and reloads again — an endless loop. If it cannot be persisted
  // (storage blocked), do not reload at all and let the error surface.
  let markerPersisted = false
  try {
    storage?.setItem(key, '1')
    markerPersisted = storage?.getItem(key) === '1'
  } catch {
    markerPersisted = false
  }

  if (!markerPersisted) {
    return false
  }

  // Prevent first so the rejected preload does not also surface as an unhandled error.
  event.preventDefault()
  const reload = deps.reload ?? (() => window.location.reload())
  reload()
  return true
}

export function registerPreloadErrorRecovery (): void {
  window.addEventListener('vite:preloadError', (event) => {
    handlePreloadError(event)
  })
}
